import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Link,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Button,
  Divider,
} from '@mui/material';
import { ArrowBack, Email, Lock } from '@mui/icons-material';
import KakaoLoginButton from '../components/KakaoLoginButton';
import { UserStatus } from '../types';
import { apiClient, API_ENDPOINTS } from '../config/api';

const LoginPage: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  // 페이지 진입 시 기존 인증 정보 정리
  React.useEffect(() => {
    apiClient.clearAuthData();
  }, []);

  const handleKakaoLoginError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // 새로운 API 클라이언트로 로그인 호출
      const loginResponse: any = await apiClient.basicAuthPost(
        API_ENDPOINTS.AUTH.LOGIN,
        email,
        password
      );
      console.log('로그인 API 응답:', loginResponse);

      // 응답 구조 확인 - result 또는 data 필드 체크
      const responseData = loginResponse.result || loginResponse.data;
      console.log('responseData:', responseData);
      
      if (!responseData) {
        console.error('응답 데이터가 없음:', loginResponse);
        throw new Error('로그인 응답 데이터가 올바르지 않습니다.');
      }

      const { userInfo, tokenDto } = responseData;
      console.log('userInfo:', userInfo);
      console.log('tokenDto:', tokenDto);

      // 액세스 토큰만 localStorage에 저장 (refreshToken은 HttpOnly 쿠키)
      localStorage.setItem('accessToken', tokenDto.accessToken);
      localStorage.setItem('user', JSON.stringify(userInfo));

      // AuthContext에 사용자 정보 설정
      login(userInfo);

      // 회원 상태에 따른 리다이렉션
      console.log('사용자 상태:', userInfo.status);
      if (userInfo.status === UserStatus.GUEST) {
        // GUEST: 프로필이 이미 존재하면서 본인인증 미완료 → 본인인증 페이지로
        const existingProfile = await apiClient.getProfile();
        if (existingProfile && !existingProfile.identityVerified) {
          navigate('/identity-verification');
        } else {
          navigate('/register/profile');
        }
      } else if (userInfo.status === UserStatus.SUSPENDED || userInfo.status === UserStatus.BANNED) {
        navigate('/sanction-block');
      } else {
        // PENDING, APPROVED, REJECTED 모든 상태는 대시보드로 이동
        // 대시보드에서 상태에 따른 적절한 화면을 보여줌
        console.log('다른 상태 - /dashboard로 이동');
        navigate('/dashboard');
      }
    } catch (err: any) {
      // HTTP 상태코드에 따른 구체적인 에러 메시지 처리
      if (err.message && err.message.includes('HTTP Error: 400')) {
        setError('이메일 또는 비밀번호가 올바르지 않습니다. 다시 확인해 주세요.');
      } else if (err.message && err.message.includes('HTTP Error: 500')) {
        setError('서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.');
      } else if (err.message && err.message.includes('HTTP Error')) {
        setError('네트워크 연결을 확인하고 다시 시도해 주세요.');
      } else {
        setError(err.message || '로그인 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div">
            로그인
          </Typography>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Silver Connect
            </Typography>
            <Typography variant="h6" color="text.secondary">
              다시 만나서 반갑습니다
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box>
            {/* 이메일 로그인 폼 */}
            <Box component="form" onSubmit={handleEmailLogin} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="이메일"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="비밀번호"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                InputProps={{
                  startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ 
                  py: 1.5, 
                  mb: 2, 
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                  }
                }}
              >
                {loading ? '로그인 중...' : '이메일로 로그인'}
              </Button>
            </Box>

            {/* 구분선 */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Divider sx={{ flexGrow: 1 }} />
              <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
                또는
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Box>

            {/* 카카오 로그인 */}
            <KakaoLoginButton
              onError={handleKakaoLoginError}
              disabled={loading}
              fullWidth
              sx={{ 
                mb: 3, 
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(254, 229, 0, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(254, 229, 0, 0.4)',
                }
              }}
            >
              카카오로 로그인
            </KakaoLoginButton>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2">
                계정이 없으신가요?{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/register');
                  }}
                  sx={{ color: 'primary.main' }}
                >
                  회원가입
                </Link>
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Silver Connect는 안전하고 간편한<br />
                로그인 서비스를 제공합니다
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;